import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ChevronDown, Search, LogOut, Menu, X } from "lucide-react";
import { ADMIN_CLUSTERS, findClusterFor, itemForRoute } from "./adminNav";
import "./admin-theme.css";

const GROUPS_KEY = "vinci.admin.nav.groups";
const readGroups = () => { try { return JSON.parse(localStorage.getItem(GROUPS_KEY)) || {}; } catch { return {}; } };

function Badge({ n, testid }) {
  if (!n) return null;
  return <span data-testid={testid} className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#C9A84C] text-[#0D0D0D] text-sm font-bold grid place-items-center">{n}</span>;
}

function NavItem({ item, active, badge, onSelect }) {
  const Icon = item.icon;
  const base = "w-full flex items-center gap-3 border-l-4 pl-4 pr-3 py-2.5 rounded-r-lg text-sm text-left transition-colors duration-200";
  const cls = active
    ? `${base} border-[#C9A84C] bg-[#C9A84C]/10 text-[#F5F5F5] font-semibold`
    : `${base} border-transparent text-[#888888] hover:text-[#F5F5F5] hover:bg-[#242424]`;
  const inner = (<><Icon size={16} className={active ? "text-[#C9A84C]" : "text-[#888888]"} /><span className="truncate">{item.label}</span>{item.tag && <span data-testid={`nav-${item.key}-tag`} className="ml-1 shrink-0 inline-flex items-center gap-1 bg-[#C9A84C] text-black font-grotesk text-[7px] uppercase tracking-[0.26em] px-1.5 py-0.5 shadow-[0_0_10px_-2px_rgba(201,168,76,0.9)]">♛ {item.tag}</span>}<Badge n={badge} testid={`${item.key}-badge`} /></>);
  return item.route
    ? <Link data-testid={`nav-${item.key}`} to={item.route} aria-current={active ? "page" : undefined} className={cls}>{inner}</Link>
    : <button data-testid={`nav-${item.key}`} onClick={() => onSelect(item.key)} aria-current={active ? "page" : undefined} className={cls}>{inner}</button>;
}

function Cluster({ cluster, open, onToggle, active, badges, onSelect }) {
  const Icon = cluster.icon;
  const hasActive = cluster.items.some((i) => i.key === active);
  return (
    <div className="mb-1">
      <button data-testid={`nav-group-${cluster.key}`} onClick={onToggle} aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-[#242424] transition-colors duration-200">
        <Icon size={18} className="text-[#C9A84C]" />
        <span className={`text-base font-bold tracking-tight ${hasActive ? "text-[#F5F5F5]" : "text-[#F5F5F5]/90"}`}>{cluster.label}</span>
        <ChevronDown size={16} className={`ml-auto text-[#888888] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 mb-3 space-y-0.5 pl-2">
          {cluster.items.map((item) => (
            <NavItem key={item.key} item={item} active={active === item.key} badge={badges[item.badge]} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminShell({ active, onSelect, badges = {}, user, onLogout, onOpenPalette, title, children }) {
  const nav = useNavigate();
  const [groups, setGroups] = useState(readGroups);
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeCluster = useMemo(() => findClusterFor(active), [active]);

  useEffect(() => {
    if (activeCluster && groups[activeCluster] === undefined) setGroups((g) => ({ ...g, [activeCluster]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCluster]);
  useEffect(() => { localStorage.setItem(GROUPS_KEY, JSON.stringify(groups)); }, [groups]);

  const isOpen = (c) => groups[c.key] === undefined ? c.key === activeCluster || c.key === "operations" : groups[c.key];
  const toggle = (c) => setGroups((g) => ({ ...g, [c.key]: !isOpen(c) }));
  const select = (key) => { setMobileOpen(false); if (onSelect) onSelect(key); else nav(key === "overview" ? "/admin" : `/admin?tab=${key}`); };
  const activeItem = ADMIN_CLUSTERS.flatMap((c) => c.items).find((i) => i.key === active);

  const sidebar = (
    <aside data-testid="admin-sidebar" className="w-72 shrink-0 h-screen sticky top-0 flex flex-col bg-[#141414] border-r border-[#262626]">
      <Link to="/" className="flex items-center gap-3 px-6 h-16 border-b border-[#262626]">
        <img src="/logo-mark.png" alt="Vinci" className="h-6 w-auto" />
        <div className="leading-tight">
          <div className="font-serif text-base text-[#F5F5F5]">Vinci Inc.</div>
          <div className="text-sm text-[#C9A84C] tracking-wide">Trade Desk Admin</div>
        </div>
      </Link>
      <nav className="flex-1 overflow-y-auto px-3 py-4 admin-scroll">
        {ADMIN_CLUSTERS.map((c) => (
          <Cluster key={c.key} cluster={c} open={isOpen(c)} onToggle={() => toggle(c)} active={active} badges={badges} onSelect={select} />
        ))}
      </nav>
      <div className="px-6 py-4 border-t border-[#262626] text-sm text-[#888888] truncate" data-testid="admin-sidebar-user">{user?.email}</div>
    </aside>
  );

  return (
    <div className="admin-shell min-h-screen flex bg-[#0D0D0D] text-[#F5F5F5]" data-testid="admin-shell">
      <div className="hidden lg:block">{sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="relative">{sidebar}</div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header data-testid="admin-topbar" className="sticky top-0 z-40 h-16 flex items-center gap-4 px-6 lg:px-10 bg-[#0D0D0D]/90 backdrop-blur-md border-b border-[#262626]">
          <button data-testid="admin-mobile-menu" onClick={() => setMobileOpen((v) => !v)} className="lg:hidden text-[#C9A84C]">{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>
          <div className="min-w-0">
            <div className="text-sm text-[#888888] truncate">{ADMIN_CLUSTERS.find((c) => c.key === activeCluster)?.label || "Admin"}</div>
            <h1 className="font-serif text-xl md:text-2xl text-[#F5F5F5] leading-tight truncate" data-testid="admin-page-title">{title || activeItem?.label || "Dashboard"}</h1>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {onOpenPalette && (
              <button type="button" data-testid="admin-srn-palette-trigger" onClick={onOpenPalette} title="Search any SRN case file"
                className="hidden sm:inline-flex items-center gap-3 rounded-lg border border-[#333333] bg-[#141414] pl-3 pr-2 py-2 text-sm text-[#888888] hover:border-[#C9A84C] hover:text-[#F5F5F5] transition-colors duration-200">
                <Search size={16} className="text-[#C9A84C]" />
                <span>Search any SRN</span>
                <kbd className="ml-1 px-2 py-0.5 border border-[#333333] rounded text-sm text-[#888888]">⌘K</kbd>
              </button>
            )}
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 rounded-lg border border-[#333333] px-4 py-2 text-sm text-[#F5F5F5] hover:border-[#C9A84C] hover:text-[#C9A84C] transition-colors duration-200">
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>
        <main className="flex-1 px-6 lg:px-10 py-8" data-testid="admin-main">{children}</main>
      </div>
    </div>
  );
}

export function AdminRouteShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const item = itemForRoute(pathname);
  if ((user?.role || "").toLowerCase() !== "admin") return children;
  return (
    <AdminShell active={item?.key} user={user} onLogout={async () => { await logout(); nav("/"); }} title={item?.label}>
      <div className="-mx-6 lg:-mx-10 -my-8 min-h-[calc(100vh-4rem)]">{children}</div>
    </AdminShell>
  );
}
